import usePopup from "@/hooks/usePopup";
import usePopupBackGuard from "@/hooks/usePopupBackGuard";
import type { TyAnchorRect, TyPopupAnim, TyPopupComp, TyPopupPlace } from "@/hooks/usePopup/types";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./styles.css";

// --- Constants ----------------------------------------------------------------------------------

const ANIM_DURATION = 200;

// --- Types --------------------------------------------------------------------------------------

type AnimState = "closed" | "open";

// --- Helpers ------------------------------------------------------------------------------------

function getViewportFlexStyles(place: TyPopupPlace): React.CSSProperties {
    switch (place) {
        case "top":
            return { alignItems: "flex-start", justifyContent: "center" };
        case "bottom":
            return { alignItems: "flex-end", justifyContent: "center" };
        case "end":
            return { alignItems: "stretch", justifyContent: "flex-end" };
        default:
            return { alignItems: "center", justifyContent: "center" };
    }
}

function getAnchorBaseStyle(anchor: TyAnchorRect): React.CSSProperties {
    const anchorCenterX = (anchor.left + anchor.right) / 2;
    const alignRight = anchorCenterX >= window.innerWidth / 2;
    return {
        minWidth: anchor.width,
        ...(alignRight ? { right: window.innerWidth - anchor.right } : { left: anchor.left }),
    };
}

// --- ViewportPopup ------------------------------------------------------------------------------

type ViewportPopupProps = {
    comp: TyPopupComp;
    animState: AnimState;
    onClose: () => void;
    zIndex: number;
};

function ViewportPopup({ comp, animState, onClose, zIndex }: ViewportPopupProps) {
    return (
        <div
            className="popup-viewport-wrap"
            style={{ zIndex, ...getViewportFlexStyles(comp.place ?? "center") }}
            onClick={comp.coverClose !== false ? onClose : undefined}
        >
            <div className="popup-content" data-popup-anim={comp.anim ?? "fade"} data-state={animState} onClick={(e) => e.stopPropagation()}>
                <comp.Component onClose={onClose} details={comp.details} />
            </div>
        </div>
    );
}

// --- AnchorPopup --------------------------------------------------------------------------------

type AnchorPopupProps = {
    comp: TyPopupComp;
    animState: AnimState;
    onClose: () => void;
    zIndex: number;
};

function AnchorPopup({ comp, animState, onClose, zIndex }: AnchorPopupProps) {
    const anchor = comp.anchor!;
    const contentRef = useRef<HTMLDivElement>(null);
    const [anim, setAnim] = useState<TyPopupAnim>(comp.anim ?? "scaleTop");
    const [posStyle, setPosStyle] = useState<React.CSSProperties>({});

    useLayoutEffect(() => {
        const base = getAnchorBaseStyle(anchor);

        const GAP = 5;
        if (comp.place === "anchor-below") {
            const popupHeight = contentRef.current?.offsetHeight ?? 0;
            const wouldOverflow = anchor.bottom + GAP + popupHeight > window.innerHeight - 8;
            if (wouldOverflow) {
                setPosStyle({ ...base, bottom: window.innerHeight - anchor.top + GAP });
                setAnim("scaleBottom");
            } else {
                setPosStyle({ ...base, top: anchor.bottom + GAP });
                setAnim(comp.anim ?? "scaleTop");
            }
        } else {
            setPosStyle({ ...base, bottom: window.innerHeight - anchor.top + GAP });
            setAnim(comp.anim ?? "scaleBottom");
        }
    }, [anchor, comp.place, comp.anim]);

    return (
        <div
            ref={contentRef}
            className="popup-content popup-anchor-content"
            data-popup-anim={anim}
            data-state={animState}
            style={{ zIndex, ...posStyle }}
        >
            <comp.Component onClose={onClose} details={comp.details} />
        </div>
    );
}

// --- PopupLayer ---------------------------------------------------------------------------------

type PopupLayerProps = {
    comp: TyPopupComp;
    index: number;
};

function PopupLayer({ comp, index }: PopupLayerProps) {
    const removeComp = usePopup((state) => state.removeComp);
    const requestClose = usePopup((state) => state.requestClose);
    const isClosing = usePopup((state) => state.closing.includes(comp.name));
    const [animState, setAnimState] = useState<AnimState>("closed");

    useEffect(() => {
        requestAnimationFrame(() => setAnimState("open"));
    }, []);

    /** Closing always travels through the store, so a back press animates out exactly like a click does. */
    useEffect(() => {
        if (!isClosing) return;
        setAnimState("closed");
        const timer = setTimeout(() => removeComp(comp.name), ANIM_DURATION);
        return () => clearTimeout(timer);
    }, [isClosing]);

    const handleClose = () => requestClose(comp.name);

    const isAnchor = comp.place === "anchor-below" || comp.place === "anchor-above";
    const backdropZIndex = 1000 + index * 2;
    const contentZIndex = 1001 + index * 2;

    return (
        <>
            <div
                className="popup-backdrop"
                data-cover={comp.cover ?? "shaded"}
                style={{ zIndex: backdropZIndex, opacity: animState === "open" ? 1 : 0 }}
                onClick={comp.coverClose !== false ? handleClose : undefined}
            />
            {isAnchor ? (
                <AnchorPopup comp={comp} animState={animState} onClose={handleClose} zIndex={contentZIndex} />
            ) : (
                <ViewportPopup comp={comp} animState={animState} onClose={handleClose} zIndex={contentZIndex} />
            )}
        </>
    );
}

// --- PopupPaper ---------------------------------------------------------------------------------

export default function PopupPaper() {
    const comps = usePopup((state) => state.comps);
    usePopupBackGuard();
    if (comps.length === 0) return null;
    return createPortal(
        <>
            {comps.map((comp, index) => (
                <PopupLayer key={comp.name} comp={comp} index={index} />
            ))}
        </>,
        document.body
    );
}
